/** The app's navigation model, in one place.
 *
 *  Both the sidebar (``Layout``) and the ⌘K palette (``CommandPalette``) read
 *  from ``NAV_SECTIONS`` through ``visibleSections`` / ``flatPages``, so the two
 *  can't drift: adding a page here puts it in the rail *and* makes it
 *  searchable. Visibility is always the server-resolved ``user.allowed_pages``
 *  set — this module never decides permissions itself.
 *
 *  All sidebar icons share one spec: 18×18 on a 24-unit viewBox, stroke-1.8
 *  outline, round joins/caps, so the rail doesn't visually wobble between rows. */

export interface NavItem {
  to: string;
  label: string;
  icon: () => JSX.Element;
  /** Keep the row lit on descendant routes the router wouldn't match. */
  activeIfStartsWith?: string[];
  /** Exact-match only — stops a parent route lighting up on its children. */
  end?: boolean;
}

export interface NavGroup {
  key: string;
  label: string;
  icon: () => JSX.Element;
  children: { to: string; label: string }[];
}

export interface NavSection {
  key: string;
  /** null → rendered without a heading (the Home row). */
  label: string | null;
  entries: (NavItem | NavGroup)[];
}

export function isGroup(e: NavItem | NavGroup): e is NavGroup {
  return "children" in e;
}

// ─── Sections ──────────────────────────────────────────────────────────────
// Order is deliberate: what you do (Work) → where leads come from (Market) →
// how it's going (Insights) → who does it (Team) → plumbing (Admin).

export const NAV_SECTIONS: NavSection[] = [
  {
    key: "start",
    label: null,
    entries: [{ to: "/home", label: "Home", icon: HomeIcon }],
  },
  {
    key: "work",
    label: "Work",
    entries: [
      {
        key: "publish",
        label: "Publish",
        icon: PublishIcon,
        children: [
          { to: "/publish", label: "Single" },
          { to: "/market/publish", label: "Bulk" },
          { to: "/competition/bulk", label: "Competition bulk" },
          { to: "/market/auto-publish", label: "Automations" },
          { to: "/publish/manual-bulk", label: "Manual bulk" },
        ],
      },
      { to: "/placements", label: "Placements", icon: ListIcon },
      { to: "/clients", label: "Clients", icon: UsersIcon },
      { to: "/deals", label: "Deals", icon: BriefcaseIcon },
    ],
  },
  {
    key: "market",
    label: "Market",
    entries: [
      { to: "/fresh", label: "Fresh", icon: SproutIcon },
      { to: "/owner-finder", label: "Owner finder", icon: TargetIcon },
    ],
  },
  {
    key: "insights",
    label: "Insights",
    entries: [
      {
        to: "/analytics/competition",
        label: "Competition",
        icon: TrophyIcon,
        activeIfStartsWith: ["/competition/"],
      },
      { to: "/leaderboard", label: "Leaderboard", icon: MedalIcon },
      { to: "/analytics/benchmarks", label: "Benchmarks", icon: ChartIcon },
      { to: "/analytics/stats", label: "Statistics", icon: PulseIcon },
    ],
  },
  {
    key: "team",
    label: "Team",
    entries: [
      { to: "/team", label: "Team", icon: UsersIcon },
      { to: "/schedule", label: "Schedule", icon: CalendarIcon },
    ],
  },
  {
    key: "admin",
    label: "Admin",
    entries: [
      { to: "/finance", label: "Finance", icon: WalletIcon },
      { to: "/vault", label: "Vault", icon: VaultIcon },
      { to: "/role-permissions", label: "Role permissions", icon: ShieldIcon },
      { to: "/street-mapping", label: "Street mapping", icon: SignpostIcon },
      { to: "/admin/jobs", label: "Job runs", icon: TerminalIcon },
      { to: "/admin/parsers", label: "Parsers", icon: PulseIcon },
      { to: "/admin/labels", label: "AI labels", icon: SparkIcon },
      { to: "/audit", label: "Audit", icon: AuditIcon },
    ],
  },
];

/** Filter the model down to what this user may open.
 *
 *  Three rules: an item whose route isn't allowed disappears; a group keeps only
 *  its allowed children, and **collapses to a plain link when exactly one
 *  survives** (a broker has only /publish — "Publish ▸" as a one-item dropdown
 *  would be silly); a section with nothing left disappears, heading and all. */
export function visibleSections(allowed: Set<string>): NavSection[] {
  return NAV_SECTIONS.map((section) => {
    const entries: (NavItem | NavGroup)[] = [];
    for (const entry of section.entries) {
      if (isGroup(entry)) {
        const children = entry.children.filter((c) => allowed.has(c.to));
        if (children.length === 0) continue;
        if (children.length === 1) {
          entries.push({
            to: children[0].to,
            label: entry.label,
            icon: entry.icon,
            end: true,
          });
        } else {
          entries.push({ ...entry, children });
        }
      } else if (allowed.has(entry.to) || entry.to === "/home") {
        // /home carries no page key — it's the one route every role can open.
        entries.push(entry);
      }
    }
    return { ...section, entries };
  }).filter((s) => s.entries.length > 0);
}

/** Flat, searchable page list for the ⌘K palette. Group children are hoisted to
 *  the top level and prefixed with the group name ("Publish · Bulk"). */
export function flatPages(
  allowed: Set<string>,
): { to: string; label: string; section: string; icon: () => JSX.Element }[] {
  const out: { to: string; label: string; section: string; icon: () => JSX.Element }[] = [];
  for (const section of visibleSections(allowed)) {
    const sectionLabel = section.label ?? "Start";
    for (const entry of section.entries) {
      if (isGroup(entry)) {
        for (const child of entry.children) {
          out.push({
            to: child.to,
            label: `${entry.label} · ${child.label}`,
            section: sectionLabel,
            icon: entry.icon,
          });
        }
      } else {
        out.push({
          to: entry.to,
          label: entry.label,
          section: sectionLabel,
          icon: entry.icon,
        });
      }
    }
  }
  return out;
}

// ─── Icons ─────────────────────────────────────────────────────────────────

export function HomeIcon() {
  return (
    <Glyph>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
      <path d="M9.5 21v-6h5v6" />
    </Glyph>
  );
}
export function SearchIcon() {
  return (
    <Glyph>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </Glyph>
  );
}
export function AuditIcon() {
  return (
    <Glyph>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </Glyph>
  );
}
export function VaultIcon() {
  return (
    <Glyph>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="12" cy="12" r="4" />
      <path d="M12 10v-1.5M12 15.5V14M14 12h1.5M8.5 12H10" />
    </Glyph>
  );
}
export function TargetIcon() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </Glyph>
  );
}
export function SproutIcon() {
  return (
    <Glyph>
      <path d="M7 20h10" />
      <path d="M10 20c5.5-2.5.8-6.4 3-10" />
      <path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z" />
      <path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z" />
    </Glyph>
  );
}
export function PublishIcon() {
  return (
    <Glyph>
      <path d="M12 19V5" />
      <polyline points="5 12 12 5 19 12" />
      <line x1="4" y1="21" x2="20" y2="21" />
    </Glyph>
  );
}
export function ListIcon() {
  return (
    <Glyph>
      <line x1="8" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="20" y2="12" />
      <line x1="8" y1="18" x2="20" y2="18" />
      <circle cx="4" cy="6" r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
    </Glyph>
  );
}
export function UsersIcon() {
  return (
    <Glyph>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Glyph>
  );
}
export function MegaphoneIcon() {
  return (
    <Glyph>
      <path d="M3 11v3a1 1 0 0 0 1 1h2.5L12 19V5L6.5 9H4a1 1 0 0 0-1 1z" />
      <path d="M16 9a3 3 0 0 1 0 6" />
      <path d="M19 6a7 7 0 0 1 0 12" />
    </Glyph>
  );
}
export function CalendarIcon() {
  return (
    <Glyph>
      <rect x="3" y="4.5" width="18" height="16" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="8" y1="2.5" x2="8" y2="6" />
      <line x1="16" y1="2.5" x2="16" y2="6" />
    </Glyph>
  );
}
export function ShieldIcon() {
  return (
    <Glyph>
      <path d="M12 2l8 3v6c0 5.25-3.5 9-8 11-4.5-2-8-5.75-8-11V5l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </Glyph>
  );
}
export function ChartIcon() {
  return (
    <Glyph>
      <line x1="3" y1="20" x2="21" y2="20" />
      <rect x="6" y="11" width="3" height="9" rx="0.5" />
      <rect x="11" y="6" width="3" height="14" rx="0.5" />
      <rect x="16" y="14" width="3" height="6" rx="0.5" />
    </Glyph>
  );
}
export function BriefcaseIcon() {
  return (
    <Glyph>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </Glyph>
  );
}
export function MedalIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="15" r="5" />
      <path d="M12 13.5 12.9 15l1.6.2-1.2 1.1.3 1.6-1.6-.8-1.6.8.3-1.6-1.2-1.1 1.6-.2Z" />
      <path d="M8.5 3.5 6 8m9.5-4.5L18 8" />
      <path d="M7 3.5h10" />
    </svg>
  );
}


export function TrophyIcon() {
  return (
    <Glyph>
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H4.5A2.5 2.5 0 0 0 7 10.5M17 6h2.5A2.5 2.5 0 0 1 17 10.5" />
      <path d="M12 14v3M8.5 21h7M9.5 21c0-2 1-2.5 2.5-2.5s2.5.5 2.5 2.5" />
    </Glyph>
  );
}
export function PulseIcon() {
  return (
    <Glyph>
      <path d="M3 12h4l2.5-6 4 13L16 12h5" />
    </Glyph>
  );
}
export function WalletIcon() {
  return (
    <Glyph>
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a1 1 0 0 1 1 1v2" />
      <rect x="3" y="7.5" width="18" height="12.5" rx="2.5" />
      <circle cx="16.5" cy="14" r="1.2" />
    </Glyph>
  );
}
export function SignpostIcon() {
  return (
    <Glyph>
      <path d="M12 3v18" />
      <path d="M5 6h11l2.5 2.5L16 11H5z" />
      <path d="M19 14H8l-2.5 2.5L8 19h11z" />
    </Glyph>
  );
}
export function SparkIcon() {
  return (
    <Glyph>
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      <circle cx="12" cy="12" r="3" />
    </Glyph>
  );
}
export function TerminalIcon() {
  return (
    <Glyph>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <polyline points="7 10 10 12.5 7 15" />
      <line x1="12.5" y1="15" x2="17" y2="15" />
    </Glyph>
  );
}
export function UserCircleIcon() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="9.5" />
      <circle cx="12" cy="10" r="3" />
      <path d="M6.5 19.6A7 7 0 0 1 12 17a7 7 0 0 1 5.5 2.6" />
    </Glyph>
  );
}
export function LogoutIcon() {
  return (
    <Glyph>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </Glyph>
  );
}
export function SunIcon() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </Glyph>
  );
}
export function MoonIcon() {
  return (
    <Glyph>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </Glyph>
  );
}

/** Shared svg shell — one place to change the icon spec. */
function Glyph({ children }: { children: React.ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0"
    >
      {children}
    </svg>
  );
}
